/**
 * Support Hooks
 * 
 * React Query hooks for support ticket management.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supportService } from '@/services/support.service';
import type { SupportTicketCreate, TicketMessageCreate, TicketStatus } from '@/types/support';
import { toast } from '@/hooks/use-toast';

const SUPPORT_TICKET_REFRESH_INTERVAL = 5000;

// Query keys
export const supportKeys = {
  all: ['support'] as const,
  tickets: () => [...supportKeys.all, 'tickets'] as const,
  ticketsList: (status?: TicketStatus, page?: number) => [...supportKeys.tickets(), { status, page }] as const,
  ticket: (id: string) => [...supportKeys.tickets(), id] as const,
  unreadCount: () => [...supportKeys.all, 'unreadCount'] as const,
};

// Get all tickets
export function useMyTickets(status?: TicketStatus, page: number = 1, pageSize: number = 20) {
  return useQuery({
    queryKey: supportKeys.ticketsList(status, page),
    queryFn: () => supportService.getMyTickets(status, page, pageSize),
  });
}

// Get single ticket with messages
export function useTicket(ticketId: string) {
  return useQuery({
    queryKey: supportKeys.ticket(ticketId),
    queryFn: () => supportService.getTicket(ticketId),
    enabled: !!ticketId,
    refetchInterval: ticketId ? SUPPORT_TICKET_REFRESH_INTERVAL : false,
    refetchIntervalInBackground: true,
  });
}

// Get unread count
export function useUnreadTicketCount() {
  return useQuery({
    queryKey: supportKeys.unreadCount(),
    queryFn: supportService.getUnreadCount,
    refetchInterval: SUPPORT_TICKET_REFRESH_INTERVAL,
    refetchIntervalInBackground: true,
  });
}

// Create ticket
export function useCreateTicket() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: SupportTicketCreate) => supportService.createTicket(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
      toast({
        title: 'Ticket criado com sucesso!',
        description: 'Nossa equipe responderá em breve.',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao criar ticket',
        description: 'Tente novamente mais tarde.',
        variant: 'destructive',
      });
    },
  });
}

// Add message to ticket
export function useAddTicketMessage(ticketId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: TicketMessageCreate) => supportService.addMessage(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportKeys.ticket(ticketId) });
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
      toast({
        title: 'Mensagem enviada!',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao enviar mensagem',
        description: 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });
}
